/**
 * Subscribes a TanStack Query client to realtime events for a board.
 *
 * On every `task.*` / `comment.*` / `sprint.*` / `column.*` / `project.*`
 * event with `version` greater than what we have locally, we either
 * (a) refetch the board snapshot, or
 * (b) apply a patch to the in-memory tasks array.
 *
 * For v1, (a) is the safe choice — patches can be added later.
 */

import { useCallback } from 'react'
import { useQueryClient } from '@tanstack/react-query'
import type { RealtimeEvent, Task } from '@friday/shared/realtime'
import { useRealtime } from '@/lib/ws'
import { invalidateAfterTransfer } from '@/features/transfers/transfers'

interface Opts {
  wsId: string | undefined
  pId: string | undefined
  onPresence?: (users: Array<{ userId: string; lastSeenAt: string }>) => void
}

export function useBoardSubscription({ wsId, pId, onPresence }: Opts) {
  const qc = useQueryClient()
  const onEvent = useCallback(
    (e: RealtimeEvent) => {
      if (
        e.t === 'task.updated' ||
        e.t === 'task.moved' ||
        e.t === 'task.created' ||
        e.t === 'task.archived' ||
        e.t === 'task.restored' ||
        e.t === 'task.deleted'
      ) {
        // Cheap path: refetch the board.
        void qc.invalidateQueries({ queryKey: ['board', wsId, pId] })
        void qc.invalidateQueries({ queryKey: ['task', e.t === 'task.created' ? e.task.id : e.taskId] })
        void qc.invalidateQueries({ queryKey: ['subtasks'] })
        void qc.invalidateQueries({ queryKey: ['my-tasks'] })
        // Workspace overview (KPI counts) gets stale whenever tasks change.
        void qc.invalidateQueries({ queryKey: ['workspace-overview', wsId] })
        return
      }
      if (e.t === 'comment.added' || e.t === 'comment.updated' || e.t === 'comment.deleted') {
        void qc.invalidateQueries({ queryKey: ['comments', e.taskId] })
        void qc.invalidateQueries({ queryKey: ['activity', 'task', e.taskId] })
        return
      }
      if (e.t === 'sprint.started' || e.t === 'sprint.completed' || e.t === 'sprint.created' || e.t === 'sprint.updated') {
        void qc.invalidateQueries({ queryKey: ['board', wsId, pId] })
        return
      }
      if (e.t === 'column.created' || e.t === 'column.updated' || e.t === 'column.archived') {
        void qc.invalidateQueries({ queryKey: ['board', wsId, pId] })
        return
      }
      if (e.t === 'project.updated' || e.t === 'project.archived' || e.t === 'project.created') {
        void qc.invalidateQueries({ queryKey: ['workspaces'] })
        void qc.invalidateQueries({ queryKey: ['workspaces', wsId, 'projects'] })
        void qc.invalidateQueries({ queryKey: ['board', wsId, pId] })
        void qc.invalidateQueries({ queryKey: ['workspace-overview', wsId] })
        return
      }
      if (e.t === 'milestone.created' || e.t === 'milestone.updated' || e.t === 'milestone.archived') {
        void qc.invalidateQueries({ queryKey: ['milestones', e.projectId] })
        void qc.invalidateQueries({ queryKey: ['board', wsId, e.projectId] })
        return
      }
      if (e.t === 'workspace.member_added' || e.t === 'workspace.member_removed') {
        // Member events carry projectId when scoped to a project (otherwise null).
        // Refresh any project-scoped member list when relevant.
        if (e.projectId) {
          void qc.invalidateQueries({ queryKey: ['project-members', e.projectId] })
          void qc.invalidateQueries({ queryKey: ['project-invites', e.projectId] })
        }
        return
      }
      if (e.t === 'transfer.completed') {
        // A board or page left or entered this workspace (FRIDAY-34).
        invalidateAfterTransfer(qc, [wsId])
        return
      }
      if (e.t === 'notification') {
        // No-op for v1; we'll wire notifications in Phase 5.
        return
      }
      // Unused vars: keep TS happy.
      void ({} as Task)
    },
    [qc, wsId, pId],
  )

  useRealtime({
    wsId,
    subscribe: pId ? [{ kind: 'project', id: pId }, { kind: 'workspace', id: wsId ?? '' }] : [],
    onEvent,
    onPresence,
  })
}
